/**
 * 基于业务的 socket 的又一层包装
 */
import MSocket, { SocketError } from '@/common/socket/socket';
import { Socket } from '@/common/socket/socket';
import { SocketPacket } from '#/net';
import { chessNameSpace } from '@/common/constant/index';
import { SocketPacketTypeEnum } from '@/enums/netEnum';
import PubSub from '@/utils/pubsub';
import { GameStatus, SocketPacketCateMap } from '@/enums/netEnum';
import { SprintPKService } from './exercise';
import { navigateTo } from '@/utils/page';
import { PageEnum } from '@/enums/pageEnum';
import { ElMessage, ElMessageBox } from 'element-plus';
import { ExerciseEnum } from '@/enums/gameEnum';
import { useGameStore } from '@/store/modules/game';
import UserService from './user';
import { useGlobalStore } from '@/store/modules/global';
import { useBasicStore } from '@/store/modules/basic';
import { showDialog } from '@/utils/dialog';
import { nextTick } from 'vue';
import { useI18n } from '@/hooks/web/useI18n';
import { setTime } from '@/utils/time';

const MS = new MSocket();
const pubsub = new PubSub();
let socket: Socket;

export function initSocket(token: string) {
    const { t } = useI18n();
    MS.destroy();
    // 设置登录的token
    MS.setToken(token);
    // 创建连接实例
    socket = MS.getSocketByName(chessNameSpace)!;
    socket.onOpen = async () => {
        // 可以发送第一个消息
        restoreSocketMessage([SocketPacketCateMap.FRIEND.INVITE_SUBJECT]);
        onSocketMessage(SocketPacketCateMap.FRIEND.INVITE_SUBJECT, (res: SocketPacket) => {
            ElMessageBox.alert(t('common.dialog.inviteContent'), t('common.dialog.invite'), {
                confirmButtonText: t('common.agree'),
                cancelButtonText: t('common.refuse'),
                type: 'info',
                draggable: false,
            })
                .then(async () => {
                    const result = await SprintPKService.joinRoom(
                        { roomId: res.data.roomId },
                        {
                            error: (res) => {
                                // TODO:需判断具体的返回码
                                ElMessage.warning(res.msg || t('components.game.roomDisband'));
                            },
                        },
                    );
                    navigateTo({
                        name: PageEnum.SPRINT_FRIEND_PK,
                        data: {
                            name: ExerciseEnum.SPRINT_FRIEND_PK,
                            config: { ...result },
                        },
                    });
                })
                .catch(() =>
                    SprintPKService.refuseInvite(
                        { roomId: res.data.roomId },
                        {
                            error: (res) => {
                                ElMessage.warning(res.msg || t('components.game.roomDisband'));
                            },
                        },
                    ),
                );
        });

        // 检测是否进入游戏房间，针对匹配状态的处理
        if (useGameStore().matchStatus) {
            useGameStore().setMatchStatus(false);
            const gameStatusResult = await UserService.getGameStatus({}, { error: () => {} });
            const gameStatus = gameStatusResult.gameStatus;
            if (gameStatus == GameStatus.IN_CHESS || gameStatus == GameStatus.IN_FRIEND_CHESS) {
                navigateTo({
                    name: PageEnum.COMPETE,
                    data: { reconnect: true },
                });
                useGameStore().setMatchStatus(false);
                restoreSocketMessage(Object.values(SocketPacketCateMap.MATCH));
            } else if (gameStatus == GameStatus.MATCH) {
                useGameStore().setMatchStatus(true);
            }
        }
    };
    socket.onMessage = (res: SocketPacket) => {
        // 更新服务器时间
        if (res.type == SocketPacketTypeEnum.PING) {
            return setTime(res?.timestamp);
        }
        handleGlobalMessage(res);
        pubsub.publish(`${res.type}.${res.category}`, res);
    };
    socket.onError = (e: any) => {
        console.log('socketError');
        if (e == SocketError.MAX_RECONNECT_COUNT) {
            if (!useGlobalStore().serverForbid) {
                ElMessageBox.alert(
                    t('common.dialog.disconnectContent'),
                    t('common.dialog.disconnect'),
                    {
                        confirmButtonText: t('common.reconnect'),
                        type: 'info',
                        draggable: false,
                    },
                ).then(() => {
                    MS.reconnect(socket);
                });
            }
        } else if (e == SocketError.INVALID_TOKEN) {
            if (!useGlobalStore().serverForbid) {
                ElMessageBox.alert('该账号已在其他设备登录，需要重新登录', '账号信息异常', {
                    confirmButtonText: '去登录',
                    type: 'info',
                    draggable: false,
                }).then(async () => {
                    showDialog((await import('@/components/Dialog/Login/Login.vue')).default, {
                        config: { single: true, zIndex: 1000 },
                    }).show();
                });
            }
        }
    };
}

export function stopSocket() {
    // 设置登录的token
    MS.setToken('');
}

export function reconnectSocket(token: string) {
    MS.setToken(token);
    MS.reconnect(socket);
}

/**
 * 重连
 */
export async function reconnectGame(back = false) {
    useGameStore().setMatchStatus(false);
    const gameStatusResult = await UserService.getGameStatus({}, { error: () => {} });
    const gameStatus = gameStatusResult.gameStatus;
    const matchType = gameStatusResult.matchType;
    if (gameStatus == GameStatus.CHAMPIONSHIP_MATCH_FINISH) {
        // 匹配完成待进入比赛
        navigateTo({
            name: matchType === 1 ? PageEnum.COMPETITION_DETAILS : PageEnum.ENTERTAINMENT_DETAILS,
            data: { id: gameStatusResult.matchId },
        });
    } else if (
        gameStatus == GameStatus.IN_CHAMPIONSHIP_MATCH ||
        gameStatus == GameStatus.WAIT_CHAMPIONSHIP_MATCH
    ) {
        navigateTo({
            name: matchType === 1 ? PageEnum.COMPETITION_DETAILS : PageEnum.ENTERTAINMENT_DETAILS,
            data: { id: gameStatusResult.matchId },
        });
    } else if (gameStatus == GameStatus.IN_CHAMPIONSHIP) {
        // 在锦标赛中
        navigateTo({
            name: matchType === 1 ? PageEnum.COMPETITION_GAME : PageEnum.ENTERTAINMENT_GAME,
            data: { reconnect: true },
        });
    } else if (gameStatus == GameStatus.IN_CHESS || gameStatus == GameStatus.IN_FRIEND_CHESS) {
        navigateTo({ name: PageEnum.COMPETE, data: { reconnect: true } });
    } else if (gameStatus == GameStatus.IN_SPRINT_PK) {
        navigateTo({
            name: PageEnum.SPRINT_FRIEND_PK,
            data: { name: ExerciseEnum.SPRINT_FRIEND_PK },
        });
    } else if (back || gameStatus == GameStatus.MATCH) {
        useGameStore().setMatchStatus(true);
    }
    useGameStore().setShowInGameIcon(false);
}

/**
 * 监听全局事件
 */
async function handleGlobalMessage(res: SocketPacket) {
    switch (`${res.type}.${res.category}`) {
        // 好友对局申请
        case SocketPacketCateMap.FRIEND_CHESS.INVITE:
            const data = res.data || {};
            const dialog = showDialog(
                (await import('@/components/Dialog/MatchInvitation.vue')).default,
                {
                    config: { single: true },
                },
            );
            dialog.show({
                avatar: data.avatar,
                level: data.rank,
                username: data.nickname,
                score: data.point,
                time: `${data.chessTime / 60}+${data.addTime}`,
                color: data.color,
                expirationTime: data.timestamp,
                initiator: false,
                sign: data.sign,
            });
            break;

        // 添加好友申请
        case SocketPacketCateMap.FRIEND.APPLY:
            // 更新好友按钮的信息提示
            useBasicStore().showFriendList = !useBasicStore().showFriendList;
            nextTick(() => {
                useBasicStore().showFriendList = !useBasicStore().showFriendList;
            });
            break;

        default:
            break;
    }
}

/**
 * 订阅事件，
 * @param key 事件的 key，对应 pubsub 的 key 值
 * @param func 需要执行的事件
 */
export function onSocketMessage(key: string, func: any) {
    pubsub.subscribe(key, func);
}

/**
 * 取消 Socket 事件订阅
 * @param list
 */
export function restoreSocketMessage(list: string[]) {
    list.forEach((item) => pubsub.unsubscribeAll(item));
}

export { socket };
